import { Col, Form, FormFeedback, FormGroup, FormProps, Input, Label, Row } from "reactstrap";
import { IWsClient, IWsClientInput } from "../../../types/ws-admin";
import { ChangeEvent, InvalidEvent, useCallback, useEffect, useState } from "react";
import PasswordInput from "../../common/password-input";
import { parseCommonError } from "../../../utils/commons";

// use the same pattern from WsAdminService
const PASSWORD_PATTERN = '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@!_%&*]).{12,}$'

interface WsClientFormProps extends FormProps {
  data?: IWsClient;
  autoFocus?: boolean;
}

const WsClientForm = (props: WsClientFormProps) => {
  const { data, autoFocus, ...formProps } = props

  const [input, setInput] = useState<IWsClientInput>()
  const [errors, setErrors] = useState<Record<string, string>>({})

  const isNew = !data?.clientId

  useEffect(() => {
    setInput(data)
    setErrors({})
  }, [data])

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setErrors({ ...errors, [name]: '' })
    setInput({ ...input, [name]: value })
  }, [errors])

  const handleInvalid = useCallback((event: InvalidEvent<HTMLInputElement>) => {
    event.preventDefault();

    const { name, validity } = event.target;

    let msg = parseCommonError(event);
    if (name === 'password' && validity.patternMismatch) {
      msg = 'A strong password should have at least 12 characters, one uppercase and lowercase letter, one number and any special character.'
    }

    setErrors({ ...errors, [name]: msg })
  }, [errors])

  return (
    <Form {...formProps}>
      <Row>
        <Col lg={isNew ? 12 : 6}>
          <FormGroup row>
            <Label for="clientName" sm={4}>
              Client Name: (required)
            </Label>
            <Col sm={8} className="pt-2">
              <Input id="clientName" name="clientName" aria-label="Client Name"
                value={input?.clientName ?? ''} onChange={handleChange} maxLength={30} invalid={!!errors.clientName}
                required
                onInvalid={handleInvalid} 
                autoFocus={autoFocus} />
              <FormFeedback>{errors.clientName}</FormFeedback>
            </Col>
          </FormGroup>
        </Col>
        <Col lg={isNew ? 12 : 6}>
          <FormGroup row>
            <Label for="clientPassword" sm={4}>
              {isNew ? 'Password: (required)' : 'Change Password:'}
            </Label>
            <Col sm={8} className="pt-2">
              <PasswordInput id="clientPassword" name="password" aria-label="Password"
                value={input?.password ?? ''} onChange={handleChange} errorMsg={errors.password} maxLength={50}
                required={isNew}
                pattern={PASSWORD_PATTERN}
                onInvalid={handleInvalid} />
            </Col>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col>
          <FormGroup row>
            <Label for="clientMemo" sm={isNew ? 4 : 2}>
              Memo:
            </Label>
            <Col sm={isNew ? 8 : 10}>
              <Input id="clientMemo" name="description" type="textarea"
                value={input?.description ?? ''} onChange={handleChange} maxLength={100} placeholder="Enter a memo for this client" />
            </Col>
          </FormGroup>
        </Col>
      </Row>
    </Form>
  )
}

export default WsClientForm
